import { Fragment, MouseEvent, useEffect, useMemo, useState } from "react";

// material-ui
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import {
  ColumnDef,
  HeaderGroup,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  useReactTable,
  SortingState,
  FilterFn,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

// project-import
import ScrollX from "components/ScrollX";
import MainCard from "components/MainCard";
import IconButton from "components/@extended/IconButton";

import {
  DebouncedInput,
  HeaderSort,
  IndeterminateCheckbox,
  RowSelection,
  SelectColumnSorting,
  TablePagination,
} from "components/@extended/react-table";

// types

import { LabelKeyObject } from "react-csv/lib/core";

// assets
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "hooks";
import Loader from "components/Loader";

import {
  MultiDeletQuestions,
  getAllQuestion,
} from "store/actions/main/basicInfo/question";
import { QuestionModel } from "model/question";
import QuestionModal from "./questionModal";
import AlertQuestionDelete from "./AlertQuestionDelete";
import ExpandingQuestionDetail from "./ExpandingQuestion";
import { Chip } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { searshAllQuestion } from "../../../../../store/actions/main/basicInfo/question";

export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // store the ranking info
  addMeta(itemRank);

  // return if the item should be filtered in/out
  return itemRank.passed;
};

// ==============================|| REACT TABLE - LIST ||============================== //

function ReactTable({
  countPage,
  fetchData,
  setPageSize,
  pageSize,
  setResult,
  currentPage,
  handlePageChange,
  data,

  columns,
  searchText,
  handleSearchChange,
  setCurrenttPage,
  modalToggler,
}: any) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "id",
      desc: true,
    },
  ]);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,

    columns,
    state: {
      sorting,
      rowSelection,
      globalFilter,
    },

    enableRowSelection: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: fuzzyFilter,
    debugTable: true,
  });
  const backColor = alpha(theme.palette.primary.light, 0.1);
  let headers: LabelKeyObject[] = [];
  columns.map(
    (columns: any) =>
      // @ts-ignore
      columns.accessorKey &&
      headers.push({
        label: typeof columns.header === "string" ? columns.header : "#",
        // @ts-ignore
        key: columns.accessorKey,
      })
  );
  const dispatch = useAppDispatch();
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [searchedtext, setsearchedtext] = useState("");
  const intl = useIntl();
  const selectedRowIds = useMemo(() => {
    // Get the selected rows from the table object
    const selectedRows = table
      .getSortedRowModel()
      .flatRows.filter((row) => row.getIsSelected());

    // Map over the selected rows to extract their IDs
    return selectedRows.map((row) => row.original.id);
  }, [table, rowSelection]);
  const handleMultiDelet = () => {
    dispatch(MultiDeletQuestions({ ids: selectedRowIds }));
    fetchData();
  };
  const searshQuestion = (searchedtext) => {
    setloadingSearsh(true);
    dispatch(searshAllQuestion({ text_trans: searchedtext })).then((action) => {
      console.log(action, "action");
      setResult(action.payload.data);
      setloadingSearsh(false);
    });
  };
  return (
    <MainCard content={false}>
      <Grid container direction={"column"}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: 2,
            ...(matchDownSM && {
              "& .MuiOutlinedInput-root, & .MuiFormControl-root": {
                width: "100%",
              },
            }),
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              variant="outlined"
              onChange={(e) => setsearchedtext(e.target.value)}
              placeholder={intl.formatMessage({
                id: "Search records..",
                defaultMessage: "Search records..",
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={() => searshQuestion(searchedtext)}
            >
              {" "}
              {loadingSearsh === false ? (
                <FormattedMessage id="Search" />
              ) : (
                <CircularProgress size="1.5rem" style={{ color: "white" }} />
              )}
            </Button>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {/* <SelectColumnSorting
              {...{
                getState: table.getState,
                getAllColumns: table.getAllColumns,
                setSorting,
              }}
            /> */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={<PlusOutlined />}
                onClick={modalToggler}
              >
                <FormattedMessage id="Add New" />
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Grid>

      <ScrollX>
        <Stack>
          <RowSelection selected={Object.keys(rowSelection).length} />
          <TableContainer>
            <Table>
              <TableHead>
                {table
                  .getHeaderGroups()
                  .map((headerGroup: HeaderGroup<any>) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        if (
                          header.column.columnDef.meta !== undefined &&
                          header.column.getCanSort()
                        ) {
                          Object.assign(header.column.columnDef.meta, {
                            className:
                              header.column.columnDef.meta.className +
                              " cursor-pointer prevent-select",
                          });
                        }

                        return (
                          <TableCell
                            key={header.id}
                            {...header.column.columnDef.meta}
                            onClick={header.column.getToggleSortingHandler()}
                            {...(header.column.getCanSort() &&
                              header.column.columnDef.meta === undefined && {
                                className: "cursor-pointer prevent-select",
                              })}
                          >
                            {header.isPlaceholder ? null : (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Box>
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </Box>
                                {header.column.getCanSort() && (
                                  <HeaderSort column={header.column} />
                                )}
                              </Stack>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          {...cell.column.columnDef.meta}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow
                        sx={{
                          bgcolor: backColor,
                          "&:hover": { bgcolor: `${backColor} !important` },
                        }}
                      >
                        <TableCell colSpan={row.getVisibleCells().length}>
                          <ExpandingQuestionDetail data={row.original} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              <TablePagination
                {...{
                  setPageSize: setPageSize,
                  setPageIndex: setCurrenttPage,
                  getState: table.getState,
                  getPageCount: () => countPage,
                  currentPage: currentPage,
                  handlePageChange: handlePageChange,
                  initialPageSize: pageSize,
                }}
              />
            </Box>
          </>
        </Stack>
      </ScrollX>
    </MainCard>
  );
}

// ==============================|| QUESTION LIST ||============================== //

const Question = () => {
  const theme = useTheme();

  //const { customersLoading, customers: lists } = useGetCustomer();

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<QuestionModel | null>(null);
  const [customerDeleteId, setCustomerDeleteId] = useState<any>("");

  const handleClose = () => {
    setOpen(!open);
  };
  const intl = useIntl();
  const columns = useMemo<ColumnDef<QuestionModel>[]>(
    () => [
      {
        header: "#",
        accessorKey: "id",
        meta: {
          className: "cell-center",
        },
      },
      {
        header: intl.formatMessage({
          id: "Arabic Question",
          defaultMessage: "Arabic Question",
        }),
        accessorKey: "text",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as string}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: intl.formatMessage({
          id: "English Question",
          defaultMessage: "English Question",
        }),
        accessorKey: "text_trans",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row.original?.text_trans?.ar}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: intl.formatMessage({ id: "Order", defaultMessage: "Order" }),
        accessorKey: "order",
        cell: ({ row, getValue }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {getValue() as number}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: intl.formatMessage({ id: "Status", defaultMessage: "Status" }),
        accessorKey: "status",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const status = cell.getValue();

          return (
            <Chip
              color={status === true ? "success" : "error"}
              label={
                status === true ? (
                  <FormattedMessage id="Active" />
                ) : (
                  <FormattedMessage id="Inactive" />
                )
              }
              size="small"
              variant="filled"
            />
          );
        },
      },
      {
        header: intl.formatMessage({
          id: "Actions",
          defaultMessage: "Actions",
        }),
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row, getValue }) => {
          return (
            <Stack
              direction="row"
              // alignItems="center"
              // justifyContent="center"
              spacing={0}
            >
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelectedCustomer(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setCustomerDeleteId(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [theme]
  );
  const [result, setResult] = useState<QuestionModel[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,

    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default value is 10, or you can set it to `initialPageSize` if available
  const [sortBy, setSortBy] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  // @ts-ignore
  const [selectedDate, setSelectedDate] = useState(null);
  // @ts-ignore
  const [searchText, setSearchText] = useState("");
  const dispatch = useAppDispatch();
  const fetchData = () => {
    setLoading(true);
    dispatch(getAllQuestion({ pageNumber: currentPage }))
      .then((action) => {
        const { data, meta } = action.payload;
        const fetchedUCountryData: QuestionModel[] = data;
        const rowsWithKeys = fetchedUCountryData.map((company) => ({
          ...company,
          key: company.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching user:", error);
      });
  };
  useEffect(() => {
    fetchData();
  }, [currentPage, dispatch, searchText]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page); // Update the currentPage state when the page changes
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <ReactTable
          setSortBy={setSortBy}
          sortBy={sortBy}
          setResult={setResult}
          selectedLanguage={selectedLanguage}
          selectedDate={selectedDate}
          setSelectedLanguage={setSelectedLanguage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchText={searchText}
          data={result}
          columns={columns}
          modalToggler={() => {
            setCustomerModal(true);
            setSelectedCustomer(null);
          }}
          countPage={countPage}
          fetchData={fetchData}
          currentPage={currentPage}
          setCurrenttPage={setCurrenttPage}
          handlePageChange={handlePageChange}
        />
      )}
      <AlertQuestionDelete
        fetchData={fetchData}
        id={customerDeleteId?.id}
        title={customerDeleteId?.text}
        open={open}
        handleClose={handleClose}
        setOpen={setOpen}
      />
      <QuestionModal
        fetchData={fetchData}
        open={customerModal}
        modalToggler={setCustomerModal}
        customer={selectedCustomer}
      />
    </>
  );
};

export default Question;
